import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { Field } from "formik";
import Select from "react-dropdown-select";

import { Label, TextBox, TextArea, DropSelect } from "modules/primitives";
import { projectListGet } from "modules/projects/project/project.action";
import NewProjectAccess from "../newProjectSharedComponents/newProjectAccess";
import NewProjectTeam from "../newProjectSharedComponents/newProjectTeam";
import NewProjectEvaluation from "../newProjectSharedComponents/newProjectEvaluation";
import styles from "./styles.module.scss";
import LoadingComponent from "modules/primitives/loading/loading.component";
import { getPlantsAndAreas } from "modules/admin/adminPlantsAndAreas/adminPlantsAndAreas.actions";
import PlantSingleSelect from "../plantSingleselect";
import { ideasSectionsGet, ideasTemplateGet } from "modules/ideasWrapper/idea.action";
import ReplicationProjectThemes from "modules/projects/project/replicateProject/replicationProjectThemes";
import {makeStages} from "../../helper";
import UserMultiSelectSearch from "../usersSearch";
import styles1 from "./styles.module.scss";
import CheckBox from "modules/admin/adminIdeas/addForm/checkBox";
import CompaniesItem from "modules/themes/themesCommon/newProjectModal/companies/companiesBlock";
import MembersItem from "modules/themes/themesCommon/newProjectModal/members/membersBlock";
import PossibleReplicationsFordModal from "./possibleReplicationsModal";

const FordNewProjectStep1 = ({ formikprops, data, proposalData, instance }) => {
  const [projects, setProjects] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const stages = makeStages(new Date().getFullYear());

  const {
    adminPlantsAndAreasReducer: { plantsAndAreas },
    councilReducer: { selectedCouncil },
    authReducer: { session },
    ideaReducer: {
      ideasTemplate,
      ideasSections,
    },
  } = useSelector((state) => state);

  useEffect(() => {
    dispatch(ideasTemplateGet({
      enqueueSnackbar,
    }));
  }, []);

  useEffect(() => {
    if (ideasTemplate?.idea_template_id) {
      dispatch(ideasSectionsGet({
        enqueueSnackbar,
        query: {
          ideas_template_id: ideasTemplate.idea_template_id,
        },
      }));
    }
  }, [ideasTemplate]);

  useEffect(() => {
    if (!plantsAndAreas.length) {
      dispatch(
        getPlantsAndAreas({
          enqueueSnackbar,
        }),
      );
    }
  }, [plantsAndAreas]);

  useEffect(() => {
    if (selectedCouncil?.id) {
      dispatch(
        projectListGet({
          councilId: selectedCouncil.id,
          pageSize: 9999,
          currentPage: 1,
          userId: session.id,
          enqueueSnackbar,
          items: 9999,
          isDropdown: true,
          original: true,
          cb: (res) => {
            setProjects(res);
          }
        })
      );
    }
  }, [selectedCouncil]);

  const isStuffDisabled = useMemo(() => {
    return (session?.council_role === "standard" || session?.council_role === "viewer") &&
      !session?.theme_investment_access;
  }, [session]);

  const getPlantsAsOptions = useCallback((filterBy) => {
    const makePlants = plantsAndAreas.map(plant => ({
      id: plant.id,
      name: plant.attributes.name,
      areas: plant.attributes.areas,
    }));
    const { values } = formikprops;
    const existingValue = values[filterBy];

    if (filterBy === 'selectedPlant' && existingValue?.id) {
      return makePlants.filter(plant => existingValue.id !== plant.id);
    }

    if (filterBy === 'selectedPlant1' && existingValue?.length) {
      return makePlants.filter(plant => !existingValue.some(p => p.id === plant.id));
    }

    return makePlants;
  }, [plantsAndAreas, formikprops.values]);

  const getSkillsTeamField = useMemo(() => {
    if (!ideasSections?.length) {
      return null;
    }

    const skillsField = ideasSections.find(field => field.section_name === 'Skills Team');
    const formattedOptions = skillsField?.section_option_values?.map((option) => ({
      label: option,
      value: option,
    })) || [];

    return (
      <div className="mb-3">
        <Label>Skills Team</Label>
        <Select
          name="skills_team"
          multi={skillsField?.multiselect}
          options={formattedOptions}
          onChange={(val) => {
            formikprops.setFieldValue('skills_team', val);
          }}
          values={formikprops.values.skills_team
            ? formikprops.values.skills_team.map(elem => formattedOptions.find(el => el.value === elem))
            : []
          }
        />
      </div>
    )
  }, [ideasSections]);

  const getWhereIdeaComeFromField = useMemo(() => {
    if (!ideasSections?.length) {
      return null;
    }

    const skillsField = ideasSections.find(field => field.section_name === 'Where did this Idea come from?');
    const formattedOptions = skillsField?.section_option_values?.map((option) => ({
      label: option,
      value: option,
    })) || [];

    return (
      <div className="mb-3">
        <Label>Where did this Idea come from?</Label>
        <Select
          name="where_did_this_idea_come_from"
          multi={skillsField?.multiselect}
          options={formattedOptions}
          onChange={(val) => {
            formikprops.setFieldValue('where_did_this_idea_come_from', val);
          }}
          values={formikprops.values.where_did_this_idea_come_from
            ? formikprops.values.where_did_this_idea_come_from.map(elem => formattedOptions.find(el => el.value === elem))
            : []
          }
        />
      </div>
    )
  }, [ideasSections]);

  const getFPSField = useMemo(() => {
    if (!proposalData) {
      return null;
    }

    const getFPSFromIdea = data?.ideas_descriptions?.find(field => field.section_name === 'FPS Category');
    const value = getFPSFromIdea ? getFPSFromIdea.section_values.map(val => ({label: val, name: val})) : null;
    const getFPSCategory = proposalData?.proposal_for_implementation?.find(f => f.field_name === 'FPS Category');
    const formattedOptions = getFPSCategory?.option_values?.map((option) => ({
      label: option,
      value: option,
    })) || [];

    if (getFPSCategory) {
      return (
        <div className="mb-3">
          <Label>{getFPSCategory.field_name}</Label>
          <Select
            name="fps_category"
            multi={getFPSCategory?.multiselect}
            options={formattedOptions}
            onChange={(val) => {
              formikprops.setFieldValue('fps_category', val);
            }}
            values={formikprops.values?.fps_category
              ? formikprops.values?.fps_category?.map(elem => formattedOptions.find(el => el.value === elem))
              : value || []
            }
          />
        </div>
      )
    }
  }, [proposalData, data]);

  const getMembers = useMemo(() => {
    if (!data.ideas_members?.length) {
      return [];
    }

    return data.ideas_members.filter(m => m.user_id !== session.id);
  }, [session, data]);

  const storeCompanies = useCallback((companyId) => {
    const { values, setFieldValue } = formikprops;

    if (values.selected_company_ids?.includes(companyId)) {
      const filteredArray = values.selected_company_ids
        .filter((selectedCompanyId) => selectedCompanyId !== companyId);

      setFieldValue('selected_company_ids', filteredArray);

      if (filteredArray.length !== data.ideas_related_companies.length) {
        setFieldValue('select_all_companies', false);
      }
    } else {
      const findCompany = data?.ideas_related_companies
        .find((c) => c.company.id === companyId);

      if (!findCompany) {
        return;
      }
      const ids = [...values.selected_company_ids, findCompany.company.id];

      if (ids.length === data.ideas_related_companies.length) {
        setFieldValue('select_all_companies', true);
      }

      setFieldValue('selected_company_ids', ids);
    }
  }, [formikprops, data]);

  const storeMembers = useCallback((memberId) => {
    const { values, setFieldValue } = formikprops;

    if (values.selected_member_ids.includes(memberId)) {
      const filteredArray = values.selected_member_ids
        .filter((selectedMemberId) => selectedMemberId !== memberId);

      setFieldValue('selected_member_ids', filteredArray || []);

      if (filteredArray.length !== getMembers.length) {
        setFieldValue('select_all_members', false);
      }
    } else {
      const findMember = getMembers.find((member) => member.user_id === memberId);

      if (!findMember) {
        return;
      }

      const ids = [...values.selected_member_ids, findMember.user_id];

      if (ids.length === getMembers.length) {
        setFieldValue('select_all_members', true);
      }

      setFieldValue('selected_member_ids', [...values.selected_member_ids, findMember.user_id]);
    }
  }, [formikprops, getMembers]);

  const selectAllCompanies = useCallback(() => {
    const newValue = !formikprops.values.select_all_companies
    formikprops.setFieldValue('select_all_companies', newValue);

    if (!newValue) {
      formikprops.setFieldValue('selected_company_ids', []);
    } else {
      formikprops.setFieldValue('selected_company_ids', data?.ideas_related_companies.map(c => c.company.id));
    }
  }, [formikprops, data]);

  const selectAllMembers = useCallback(() => {
    const newValue = !formikprops.values.select_all_members
    formikprops.setFieldValue('select_all_members', newValue);

    if (!newValue) {
      formikprops.setFieldValue('selected_member_ids', []);
    } else {
      formikprops.setFieldValue('selected_member_ids', getMembers.map(member => member.user_id));
    }
  }, [formikprops, getMembers]);

  return (
    <div className="d-flex flex-column w-100">
      <Label>Project name</Label>
      <TextBox
        type="text"
        name="name"
        placeholder="Project name"
        formProps={formikprops}
        className="w-100"
      />
      <div className="mb-3">
        <NewProjectAccess
          formikprops={formikprops}
          isDisabled={isStuffDisabled}
        />
      </div>
      {formikprops.values.visibility === "team" && (
        <div className={styles.formElement}>
        <NewProjectTeam setFieldValue={formikprops.setFieldValue} />
        </div>
      )}
      <NewProjectEvaluation formikprops={formikprops} />
      <>
        <div className="mb-3">
          <PlantSingleSelect formikprops={formikprops} plants={getPlantsAsOptions('selectedPlant1')} />
          {formikprops.errors['selectedPlant'] && (
            <div className={styles.invalidFeedback}>{formikprops.errors['selectedPlant']}</div>
          )}
        </div>
        <div className="d-flex flex-column w-100 mb-3">
          <Label>Is this a Replication?</Label>
          <div className="d-flex align-items-center w-100 ml-5">
            <div className="w-25 position-relative">
              <label className={styles.formCheckLabel} htmlFor="originalOrReplicationYes">
                <Field
                  id="originalOrReplicationYes"
                  name="originalOrReplicationYes"
                  type="radio"
                  className={styles.formCheckInput}
                  checked={formikprops.values.isReplication}
                  onChange={() => formikprops.setFieldValue('isReplication', true)}
                />
                Yes
              </label>
            </div>
            <div className="w-25 position-relative">
              <label className={styles.formCheckLabel} htmlFor="originalOrReplicationNo">
                <Field
                  id="originalOrReplicationNo"
                  name="originalOrReplicationNo"
                  type="radio"
                  className={styles.formCheckInput}
                  checked={!formikprops.values.isReplication}
                  onChange={() => formikprops.setFieldValue('isReplication', false)}
                />
                No
              </label>
            </div>
          </div>
        </div>
        {
          formikprops.values.isReplication && (
            <div className="mb-3">
              {
                !projects ? (
                  <LoadingComponent customText="Getting Projects..." />
                ) : (
                  <>
                    <Label>Select Original Project</Label>
                    <DropSelect
                      onChange={(value) => {
                        formikprops.setFieldValue('project_id', value.val);
                      }}
                      placeholder="Select Original Project"
                      labelField="name"
                      valueField="id"
                      searchBy="name"
                      options={projects || []}
                      values={data.original_project?.id ? [{
                        id: data.original_project.id,
                        name: data.original_project.name,
                      }] : formikprops.values.project_id && projects
                        ? projects?.filter(p => p.id === formikprops.values.project_id)
                        : []
                      }
                    />
                  </>
                )
              }
            </div>
          )
        }
        {
          instance === 'idea' && (
            <div className="mb-5">
              <PossibleReplicationsFordModal data={data} formikProps={formikprops} />
            </div>
          )
        }
        {
          projects.length ? (
            <div className="mb-3">
              <Label>Select Similar Projects</Label>
              <Select
                label="Select Similar Projects"
                placeholder="Select Similar Projects"
                labelField="name"
                searchBy="name"
                valueField="id"
                name="similar_projects"
                multi={true}
                options={projects}
                onChange={(val) => {
                  formikprops.setFieldValue("similar_projects", val);
                }}
                values={formikprops.values.similar_projects?.length ? formikprops.values.similar_projects : []}
              />
            </div>
          ) : null
        }
        <div className="mb-2">
          <Label>Description</Label>
          <TextArea
            type="text"
            name="description"
            placeholder="Describe the problem you are trying to solve, the project goal, or description."
            className="w-100"
            formProps={formikprops}
            rows={4}
          />
        </div>
        <div className="mb-2">
          <Label>Problem Statement</Label>
          <TextArea
            type="text"
            name="problem_statement"
            placeholder="Problem Statement"
            className="w-100"
            formProps={formikprops}
            rows={4}
          />
        </div>
        <div className="mb-2">
          <Label>Potential Benefits</Label>
          <TextArea
            type="text"
            name="potential_benefits"
            placeholder="Potential Benefits"
            className="w-100"
            formProps={formikprops}
            rows={4}
          />
        </div>
        <div className="mb-3">
          <ReplicationProjectThemes
            name="Innovation Pillar"
            handleSelect={(themes) => {
              formikprops.setFieldValue("themes", themes)
            }}
            themes={formikprops.values.themes || []}
          />
        </div>
        {getSkillsTeamField}
        {getWhereIdeaComeFromField}
        {getFPSField}
        <div className="mb-3">
          <Label>Next Stage Change</Label>
          <Select
            label="Next Stage Change"
            placeholder="Next Stage Change"
            labelField="name"
            valueField="id"
            name="next_stage_change"
            multi={false}
            options={stages}
            onChange={(val) => {
              formikprops.setFieldValue('next_stage_change', val[0]);
            }}
            values={formikprops.values.next_stage_change ? [formikprops.values.next_stage_change] : []}
          />
        </div>
      </>
      <div className="mb-3">
        <Label>Select Project Owner</Label>
        <UserMultiSelectSearch
          multi
          placeholder="Search for project owner"
          labelField="full_name"
          valueField="id"
          searchBy="full_name"
          hasAll={false}
          onChange={(value) => {
            formikprops.setFieldValue('project_owners', value);
          }}
          preselectCurrentUser
        />
        {formikprops.errors['project_owners'] && (
          <div className={styles.invalidFeedback}>{formikprops.errors['project_owners']}</div>
        )}
      </div>
      {
        formikprops.values.project_stage && instance === 'idea' && (
          <div>
            {
              data?.ideas_related_companies?.length > 0 ? (
                <>
                  <Label>Select company</Label>
                  <div className={styles1.selectAllCompanies}>
                    <Label>Select all companies</Label>
                    <CheckBox
                      onChange={selectAllCompanies}
                      className={styles1.selectCompCheckbox}
                      isChecked={formikprops.values.select_all_companies}
                    />
                  </div>
                  <div className={styles1.companiesBlockContainer}>
                    {
                      data?.ideas_related_companies.filter(c => !c.is_product).map((company) => (
                        <div key={`${company.company.id}`}>
                          <CompaniesItem
                            storeCompanies={storeCompanies}
                            company={{
                              ...company.company,
                              company_id: company.company.id
                            }}
                            selectAllCompanies={formikprops.values.select_all_companies}
                            selected={formikprops.values.selected_company_ids.includes(company.company.id)}
                          />
                        </div>
                      ))
                    }
                  </div>
                </>
              ) : null
            }
            {
              getMembers?.length > 0 ? (
                <div className="mt-3 mb-3">
                  <Label>Select member</Label>
                  <div className={styles1.selectAllCompanies}>
                    <Label>Select all members</Label>
                    <CheckBox
                      onChange={selectAllMembers}
                      className={styles1.selectMemberCheckbox}
                      isChecked={formikprops.values.select_all_members}
                    />
                  </div>
                  <div className={styles1.membersBlockContainer}>
                    {
                      getMembers.map((member) => (
                        <div
                          key={`${member.user_id}`}
                          className={styles1.companiesBlockItem}
                        >
                          <MembersItem
                            storeMembers={storeMembers}
                            member={{
                              ...member,
                              avatar: member.user_avatar,
                              name: member.user_first_name
                            }}
                            selectAllMembers={formikprops.values.select_all_members}
                            selected={formikprops.values.selected_member_ids.includes(member.user_id)}
                          />
                        </div>
                      ))
                    }
                  </div>
                </div>
              ) : null
            }
          </div>
        )
      }
    </div>
  );
};

export default FordNewProjectStep1;
